(() => {
  'use strict';

  if (window.DeebSignatureSetQuantitySafety) return;

  const ROOT_SELECTOR = '[data-deeb-signature-purchase]';
  const INPUT_SELECTOR = '.quantity__input[name="quantity"]';
  const PRIMARY_SUBMIT_SELECTOR = '[data-deeb-signature-primary-submit]';

  class SignatureSetQuantityGuard {
    constructor(root) {
      this.root = root;
      this.input = root.querySelector(INPUT_SELECTOR);
      this.form = this.input?.form || null;
      this.productForm = this.form?.closest('product-form') || null;
      this.primarySubmit = root.querySelector(PRIMARY_SUBMIT_SELECTOR);
      this.guardOwnsSubmitState = false;
      this.previousSubmitState = null;
      this.destroyed = false;

      if (!this.ready) return;

      this.input.required = true;
      this.root.addEventListener('input', this.handleQuantityEvent, true);
      this.root.addEventListener('change', this.handleQuantityEvent, true);
      this.root.addEventListener('focusout', this.handleQuantityBlur, true);
      this.form.addEventListener('submit', this.handleSubmit, true);
      this.validate({ showMessage: false });
    }

    get ready() {
      return Boolean(
        !this.destroyed &&
          this.root?.isConnected &&
          this.input &&
          this.form &&
          this.productForm &&
          this.primarySubmit
      );
    }

    matchesCurrentControls() {
      if (this.destroyed || !this.root?.isConnected) return false;

      const input = this.root.querySelector(INPUT_SELECTOR);
      const form = input?.form || null;
      const productForm = form?.closest('product-form') || null;
      const primarySubmit = this.root.querySelector(PRIMARY_SUBMIT_SELECTOR);

      return (
        input === this.input &&
        form === this.form &&
        productForm === this.productForm &&
        primarySubmit === this.primarySubmit
      );
    }

    handleQuantityEvent = (event) => {
      if (event.target !== this.input) return;
      this.validate({ showMessage: event.type === 'change' });
    };

    handleQuantityBlur = (event) => {
      if (event.target !== this.input) return;
      this.validate({ showMessage: true });
    };

    handleSubmit = (event) => {
      if (event.target !== this.form) return;
      if (this.validate({ showMessage: true })) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.input.focus();
    };

    validate({ showMessage = false } = {}) {
      if (!this.ready) return true;

      const quantity = this.input.valueAsNumber;
      const valid = Number.isFinite(quantity) && this.input.checkValidity();

      if (valid) {
        this.input.removeAttribute('aria-invalid');
        this.root.removeAttribute('data-quantity-invalid');
        this.releaseSubmitState();
        this.productForm.handleErrorMessage?.();
        return true;
      }

      this.input.setAttribute('aria-invalid', 'true');
      this.root.setAttribute('data-quantity-invalid', 'true');
      this.takeSubmitState();

      if (showMessage) {
        const message = this.input.validationMessage || 'Enter a valid quantity.';
        this.productForm.handleErrorMessage?.(message);
      }

      return false;
    }

    takeSubmitState() {
      if (!this.ready) return;

      if (!this.guardOwnsSubmitState) {
        this.previousSubmitState = {
          disabled: this.primarySubmit.disabled,
          ariaDisabled: this.primarySubmit.getAttribute('aria-disabled'),
        };
        this.guardOwnsSubmitState = true;
      }

      this.primarySubmit.disabled = true;
      this.primarySubmit.setAttribute('aria-disabled', 'true');
    }

    releaseSubmitState() {
      if (this.destroyed || !this.primarySubmit || !this.guardOwnsSubmitState) return;

      if (this.previousSubmitState?.disabled) this.primarySubmit.disabled = true;
      else this.primarySubmit.removeAttribute('disabled');

      if (this.previousSubmitState?.ariaDisabled === null) {
        this.primarySubmit.removeAttribute('aria-disabled');
      } else if (this.previousSubmitState) {
        this.primarySubmit.setAttribute('aria-disabled', this.previousSubmitState.ariaDisabled);
      }

      this.guardOwnsSubmitState = false;
      this.previousSubmitState = null;
    }

    handlePageShow() {
      this.validate({ showMessage: false });
    }

    destroy() {
      if (this.destroyed) return;

      this.releaseSubmitState();
      this.root?.removeEventListener('input', this.handleQuantityEvent, true);
      this.root?.removeEventListener('change', this.handleQuantityEvent, true);
      this.root?.removeEventListener('focusout', this.handleQuantityBlur, true);
      this.form?.removeEventListener('submit', this.handleSubmit, true);

      this.destroyed = true;
      this.root = null;
      this.input = null;
      this.form = null;
      this.productForm = null;
      this.primarySubmit = null;
      this.previousSubmitState = null;
      this.guardOwnsSubmitState = false;
    }
  }

  const instances = new Map();

  const destroyRoot = (root) => {
    const instance = instances.get(root);
    if (!instance) return;
    instance.destroy();
    instances.delete(root);
  };

  const initializeRoot = (root) => {
    if (!(root instanceof HTMLElement) || !root.matches(ROOT_SELECTOR)) return null;

    const existing = instances.get(root);
    if (existing?.matchesCurrentControls()) return existing;
    if (existing) destroyRoot(root);

    const instance = new SignatureSetQuantityGuard(root);
    if (!instance.ready) {
      instance.destroy();
      return null;
    }

    instances.set(root, instance);
    return instance;
  };

  const initializeAll = (scope = document) => {
    if (scope instanceof HTMLElement && scope.matches(ROOT_SELECTOR)) initializeRoot(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initializeRoot);
  };

  const destroyWithin = (scope) => {
    instances.forEach((instance, root) => {
      if (!root.isConnected || root === scope || scope?.contains?.(root)) {
        instance.destroy();
        instances.delete(root);
      }
    });
  };

  const validateAll = ({ showMessage = false } = {}) => {
    instances.forEach((instance, root) => {
      if (!instance.matchesCurrentControls()) {
        const connected = root.isConnected;
        destroyRoot(root);
        if (connected) initializeRoot(root);
        return;
      }
      instance.validate({ showMessage });
    });
  };

  document.addEventListener('shopify:section:load', (event) => {
    destroyWithin(event.target);
    initializeAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroyWithin(event.target);
  });

  window.addEventListener('pageshow', () => {
    initializeAll();
    instances.forEach((instance) => instance.handlePageShow());
  });

  initializeAll();

  window.DeebSignatureSetQuantitySafety = Object.freeze({
    validate: validateAll,
    get activeInstances() {
      return instances.size;
    },
  });
})();
